import { Copy, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { openInSystemBrowser } from "../../../firebase/oauthEnv";

/**
 * Shown when Google is tapped inside Instagram / Facebook / TikTok / an
 * Android WebView: Google blocks sign-in there ("403 disallowed_useragent"),
 * so we send the visitor to Safari/Chrome on the same page instead of letting
 * them hit Google's error screen.
 */
export function InAppBrowserDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(t("authFlow.inApp.copied", "Ссылка скопирована — вставьте её в Safari или Chrome"));
    } catch {
      toast.info(window.location.href);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-end justify-center bg-slate-900/40 backdrop-blur-sm sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="in-app-browser-title"
      onClick={onClose}
    >
      <div
        className="w-full rounded-t-3xl bg-white px-6 pt-6 shadow-2xl sm:max-w-md sm:rounded-3xl"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px) + 24px, 32px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
          <ExternalLink size={24} />
        </div>
        <h2 id="in-app-browser-title" className="text-xl font-bold text-gray-900">
          {t("authFlow.inApp.title", "Откройте сайт в браузере")}
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          {t(
            "authFlow.inApp.text",
            "Google не разрешает входить внутри Instagram, Facebook и других приложений. Откройте turaq.kg в Safari или Chrome — вход займёт пару секунд.",
          )}
        </p>

        <div className="mt-5 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => {
              if (!openInSystemBrowser()) copyLink();
            }}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-blue-700 text-[15px] font-semibold text-white transition hover:bg-blue-800 active:scale-[.99]"
          >
            <ExternalLink size={18} />
            {t("authFlow.inApp.open", "Открыть в браузере")}
          </button>
          <button
            type="button"
            onClick={copyLink}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white text-[15px] font-semibold text-gray-800 transition hover:bg-gray-50 active:scale-[.99]"
          >
            <Copy size={18} className="text-blue-600" />
            {t("authFlow.inApp.copy", "Скопировать ссылку")}
          </button>
        </div>

        <p className="mt-4 text-center text-[13px] leading-snug text-gray-500">
          {t(
            "authFlow.inApp.hint",
            "Или нажмите ⋯ в углу экрана → «Открыть в браузере». Можно войти и по email ниже.",
          )}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="mt-2 h-11 w-full text-[15px] font-medium text-gray-500 hover:text-gray-700"
        >
          {t("common.close", "Закрыть")}
        </button>
      </div>
    </div>
  );
}
